import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { Code2, Smartphone, ShoppingCart, Layers, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { serviceSchema, faqSchema } from "@/data/schema";

const STACK = [
  { icon: ShoppingCart, title: "D2C Storefronts", desc: "Shopify, WooCommerce or custom Next.js storefronts. Built for SEO, speed and conversion." },
  { icon: Smartphone, title: "Mobile Apps", desc: "Native iOS & Android apps. Push notifications, loyalty, AR try-on — whatever your category demands." },
  { icon: Layers, title: "Headless Commerce", desc: "Decoupled storefront + commerce backend. For brands with custom UX and complex catalogs." },
  { icon: Code2, title: "Marketplace Integrations", desc: "Connect your storefront, ERP and marketplace catalogs into one inventory + order pipeline." },
];

const CASES = [
  { brand: "Wellnest", outcome: "Custom Shopify + iOS app. 3.2x repeat rate." },
  { brand: "Avani", outcome: "Headless Next.js storefront. 60% faster checkout." },
  { brand: "Forge", outcome: "Marketplace + storefront + WMS in 90 days." },
];

const PROCESS = [
  { t: "Discovery", d: "Goals, catalogue size, integrations, markets (India, USA, UAE) and budget agreed in a written scope." },
  { t: "Design", d: "Mobile-first page designs for home, collection, product, cart and checkout, reviewed with you before build." },
  { t: "Build", d: "Shopify, WooCommerce or custom Next.js development with payments, shipping, tax and analytics set up." },
  { t: "Launch", d: "Speed, SEO and checkout testing, redirects from any old site, Google Search Console and analytics live." },
  { t: "Grow", d: "Ongoing updates, conversion improvements and marketplace catalogue sync after launch." },
];

const FAQS = [
  { q: "Which platform should my e-commerce website use?", a: "Shopify suits most D2C brands that want to launch fast and manage the store themselves. WooCommerce suits brands already on WordPress. A custom Next.js build suits brands with complex catalogues, custom UX or high traffic." },
  { q: "Do you build websites for brands in the USA and UAE?", a: "Yes. We set up local currency, payment options, shipping rates and tax settings for India, the USA and the UAE, and Arabic-ready layouts for UAE stores." },
  { q: "Can the website sync with Amazon and Flipkart?", a: "Yes. We connect the storefront, inventory and marketplace catalogues so stock and orders stay in one pipeline." },
  { q: "Is SEO included in website development?", a: "Yes. Every site launches with clean URLs, page titles and descriptions, structured data, an XML sitemap, fast mobile pages and Search Console set up." },
];

export default function WebAppDev() {
  const schema = [
    serviceSchema({
      name: "E-commerce Website & App Development",
      description: "Shopify, WooCommerce and custom React/Next.js e-commerce websites and iOS and Android apps for brands in India, the USA and the UAE.",
      path: "/web-app-development",
      serviceType: "E-commerce website development",
    }),
    faqSchema(FAQS),
  ];
  return (
    <>
      <SEO
        title="Website & App Development — D2C Storefronts, Mobile Apps & Headless"
        description="Premium D2C storefronts, iOS & Android apps, and headless commerce architecture. Shopify, Next.js, React Native — built for speed and scale."
        path="/web-app-development"
        schema={schema}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: "Website & App Development", path: "/web-app-development" }]}
      />

      <section className="border-b-2 border-black bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 md:grid-cols-12 md:px-8">
          <div className="md:col-span-7">
            <div className="tape">Web & App Development</div>
            <h1 className="display mt-6 text-5xl font-black leading-[0.95] tracking-tight md:text-6xl lg:text-7xl">
              Storefronts that <span className="bg-[#FFD700] px-2">convert.</span><br />
              Apps that <span className="underline decoration-[#FFD700] decoration-8 underline-offset-8">retain.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base text-black/70 md:text-lg">
              Modern, performant Shopify, WooCommerce, and custom React/Next.js storefronts. Native iOS & Android apps. Logistics, payments, and your marketplace catalogs — fully integrated.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact/?type=devproject" className="btn-brutal" data-testid="webapp-cta-quote">
                Get a Quote <ArrowUpRight size={16} />
              </Link>
              <Link to="/contact/?type=expert" className="btn-brutal secondary">Talk to a tech lead</Link>
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="card-brutal overflow-hidden p-0">
              <img
                src="https://images.unsplash.com/photo-1603969280040-3bbb77278211?crop=entropy&cs=srgb&w=900&q=70&auto=format"
                width="900"
                height="600"
                alt="Developer building an e-commerce website and mobile app"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#FAFAF7] py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="eyebrow">What we build</div>
          <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl">From simple Shopify to full-stack commerce.</h2>
          <div className="mt-10 grid grid-cols-1 gap-0 md:grid-cols-2">
            {STACK.map((s, i) => (
              <div
                key={s.title}
                data-testid={`webapp-stack-${i}`}
                className="-ml-[2px] -mt-[2px] flex gap-5 border-2 border-black bg-white p-8 transition-colors hover:bg-[#FFD700]"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center border-2 border-black bg-[#FFD700]">
                  <s.icon size={22} />
                </div>
                <div>
                  <h3 className="display text-xl font-black">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-black/75">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-black py-20 text-white md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="eyebrow text-[#FFD700]">Selected Outcomes</div>
          <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl">A few projects we shipped.</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {CASES.map((c) => (
              <div key={c.brand} className="border-2 border-[#FFD700] bg-black p-8">
                <div className="display text-2xl font-black text-[#FFD700]">{c.brand}</div>
                <p className="mt-4 text-sm text-white/80">{c.outcome}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="eyebrow">How we build</div>
          <h2 className="display mt-3 text-3xl font-black tracking-tight md:text-4xl">E-commerce websites for brands in India, the USA and the UAE.</h2>
          <ol className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {PROCESS.map((p, i) => (
              <li key={p.t} className="card-brutal">
                <div className="text-xs font-bold uppercase tracking-widest text-black/60">Step {i + 1}</div>
                <h3 className="display mt-2 text-xl font-black">{p.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-black/75">{p.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-y-2 border-black bg-[#FAFAF7] py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">Website development FAQ</h2>
          <div className="mt-8 space-y-4">
            {FAQS.map((f) => (
              <details key={f.q} className="card-brutal">
                <summary className="cursor-pointer text-base font-bold md:text-lg">{f.q}</summary>
                <p className="mt-3 text-sm text-black/80 md:text-base">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        title="Have a project in mind?"
        subtitle="Tell us about your storefront, app or integration. We respond within one working day."
        primaryLabel="Start a Project"
        primaryTo="/contact/?type=devproject"
      />
    </>
  );
}
